import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, Download } from 'lucide-react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Link } from 'react-router-dom';

const ShareModal = ({ isOpen, onClose, shareUrl, profile }) => {
  const [copied, setCopied] = useState(false);
  const modalRef = useRef(null);
  const qrCodeRef = useRef(null);

  const { profile_image_url, display_name = '대표 문구' } = profile || {};

  const getImageUrl = (filename) => {
    if (!filename) return null;
    if (filename.startsWith('blob:')) return filename;
    return `/uploads/${filename}`;
  };

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    navigator.clipboard.writeText(shareUrl).then(() => setCopied(true));
  }, [isOpen, shareUrl]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => e.key === 'Escape' && onClose();

    if (isOpen) {
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCopyClick = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadQrCode = (type) => {
    const canvas = qrCodeRef.current?.querySelector('canvas');
    const svg = qrCodeRef.current?.querySelector('svg');
    if (type === 'png' && canvas) {
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${display_name}.png`;
      link.click();
    } else if (type === 'svg' && svg) {
      const data = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([data], { type: 'image/svg+xml' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${display_name}.svg`;
      link.click();
      URL.revokeObjectURL(link.href);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
      <div
        ref={modalRef}
        className="relative bg-gradient-to-br from-white/90 to-gray-50/80 rounded-2xl shadow-2xl p-8 w-full max-w-sm sm:max-w-md border border-white/40 backdrop-blur-xl animate-scaleIn"
      >
        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          aria-label="닫기"
          className="absolute top-3 right-3 p-2 rounded-full bg-white/60 hover:bg-white transition-colors shadow-md"
        >
          <X size={20} className="text-gray-700" />
        </button>

        {/* 프로필 */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={getImageUrl(profile_image_url) || 'https://placehold.co/96x96?text=IMG'}
            alt="프로필"
            className="w-24 h-24 rounded-full object-cover shadow-lg border-4 border-white/70"
          />
          <p className="mt-3 text-lg font-semibold text-gray-800">{display_name}</p>
        </div>

        {/* 링크 + 복사 버튼 */}
        <div className="bg-white/60 border border-white/40 rounded-xl p-3 flex items-center shadow-inner mb-6">
          <input
            value={shareUrl}
            readOnly
            className="flex-grow bg-transparent text-gray-700 text-sm text-center outline-none truncate"
          />
          <button
            onClick={handleCopyClick}
            className="ml-2 flex items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-[#5865F2] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#4a56d1]"
            style={{ minWidth: '84px' }} // 🔹 글자 줄바꿈 방지
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span>{copied ? '복사됨' : '복사'}</span>
          </button>
        </div>

        {/* QR 코드 */}
        <div
          className="flex flex-col items-center mb-6 bg-white/60 border border-white/50 backdrop-blur-md rounded-2xl shadow-inner p-5"
          ref={qrCodeRef}
        >
          <QRCodeSVG value={shareUrl} size={180} level="H" includeMargin />
          <QRCodeCanvas
            value={shareUrl}
            size={180}
            level="H"
            includeMargin
            style={{ display: 'none' }}
          />
        </div>

        {/* 다운로드 버튼 */}
        <div className="flex justify-center gap-3 mb-6">
          <button
            onClick={() => downloadQrCode('png')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/70 border border-gray-300 hover:bg-gray-100 transition-all text-gray-700 text-sm font-medium shadow-sm"
          >
            <Download size={16} /> PNG
          </button>
          <button
            onClick={() => downloadQrCode('svg')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/70 border border-gray-300 hover:bg-gray-100 transition-all text-gray-700 text-sm font-medium shadow-sm"
          >
            <Download size={16} /> SVG
          </button>
        </div>

        {/* 하단 */}
        <div className="text-center text-sm text-gray-600">
          만약 내 사이트를 무료로 만들 수 있다면?{' '}
          <Link to="/design" className="text-blue-600 font-semibold hover:underline">
            Disparty!
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
